/**
 * PECE PRS Tab — Phase 2.B Step 8.A
 *
 * Per-project Procurement Readiness Score dashboard:
 *   • Project PRS card (with label + weighting switch)
 *   • Top 5 At-Risk activities (with diagnostic drawer)
 *   • All-activities table sorted worst-first
 *   • Inline action buttons → one-click CREATE_PPR_MATERIAL via
 *     /api/pece/ppr/from-suggestion (closes the Insight → Action loop)
 */
import React, { useEffect, useState } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  Activity, AlertTriangle, CheckCircle2, ChevronRight, Loader2,
  Sparkles, RefreshCw, ListChecks, Wand2, X, ExternalLink,
} from 'lucide-react';

const TIER_COLORS = {
  ready_to_execute: { bg: 'bg-emerald-50', border: 'border-emerald-300', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  ready_soon: { bg: 'bg-amber-50', border: 'border-amber-300', text: 'text-amber-700', dot: 'bg-amber-500' },
  risk: { bg: 'bg-orange-50', border: 'border-orange-300', text: 'text-orange-700', dot: 'bg-orange-500' },
  not_ready: { bg: 'bg-rose-50', border: 'border-rose-300', text: 'text-rose-700', dot: 'bg-rose-500' },
};

const PROJECT_LABEL_COLORS = {
  excellent: 'text-emerald-700 bg-emerald-50 border-emerald-300',
  on_track: 'text-amber-700 bg-amber-50 border-amber-300',
  at_risk: 'text-orange-700 bg-orange-50 border-orange-300',
  critical: 'text-rose-700 bg-rose-50 border-rose-300',
};

const URGENCY_PILLS = {
  high: 'bg-rose-100 text-rose-700 border-rose-300',
  medium: 'bg-amber-100 text-amber-700 border-amber-300',
  low: 'bg-slate-100 text-slate-700 border-slate-300',
};

const ACTION_ICONS = {
  CREATE_PPR_MATERIAL: '📦',
  ASSIGN_LABOUR: '👷',
  ALLOCATE_EQUIPMENT: '🚜',
  DEFINE_RESOURCE_PLAN: '📋',
};

const fmtPct = (n) => `${Number(n || 0).toFixed(1)}%`;
const fmtConfidence = (c) => `${Math.round((c || 0) * 100)}%`;

const PECEPRSTab = ({ projectId }) => {
  const [project, setProject] = useState(null);
  const [activities, setActivities] = useState([]);
  const [atRisk, setAtRisk] = useState([]);
  const [weightBy, setWeightBy] = useState('duration_days');
  const [loading, setLoading] = useState(true);
  const [drawerActivity, setDrawerActivity] = useState(null);
  const [executingAction, setExecutingAction] = useState(null);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [proj, acts, ar] = await Promise.all([
        axiosInstance.get(`/pece/projects/${projectId}/prs/project?weight_by=${weightBy}`),
        axiosInstance.get(`/pece/projects/${projectId}/prs/activities`),
        axiosInstance.get(`/pece/projects/${projectId}/prs/at-risk?limit=5`),
      ]);
      setProject(proj.data);
      setActivities(acts.data);
      setAtRisk(ar.data.items || []);
    } catch (e) {
      toast.error('فشل تحميل بيانات PRS');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [projectId, weightBy]);

  const executeAction = async (activityCode, actionCode) => {
    // Only CREATE_PPR_MATERIAL is wired. Other action codes have their
    // button disabled at the UI level (handled in ActionDrawer), so we
    // shouldn't reach here for them — but guard defensively.
    if (actionCode !== 'CREATE_PPR_MATERIAL') return;
    setExecutingAction(`${activityCode}:${actionCode}`);
    try {
      const r = await axiosInstance.post(`/pece/ppr/from-suggestion`, {
        project_id: projectId,
        activity_code: activityCode,
        action_type: actionCode,
      });
      toast.success(`تم إنشاء PPR رقم ${r.data.ppr.ppr_no} (${r.data.items_generated} مادة)`);
      setDrawerActivity(null);
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فشل تنفيذ الإجراء');
    } finally {
      setExecutingAction(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-500" data-testid="prs-tab-loading">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> جاري تحميل PRS...
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="pece-prs-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs text-indigo-600 font-semibold uppercase tracking-wider mb-1">
            Procurement Readiness Score (PRS)
          </div>
          <h2 className="text-2xl font-bold text-gray-900">جاهزية تنفيذ المشروع</h2>
        </div>
        <div className="flex items-center gap-3">
          <select
            data-testid="prs-weight-by"
            value={weightBy}
            onChange={(e) => setWeightBy(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
          >
            <option value="duration_days">Weight: Duration Days</option>
            <option value="contract_value">Weight: Contract Value</option>
          </select>
          <button
            data-testid="prs-refresh-btn"
            onClick={loadAll}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" /> تحديث
          </button>
        </div>
      </div>

      {/* Project PRS Card */}
      <ProjectPRSCard project={project} />

      {/* S2P Recommendations Strip — Sprint A */}
      <S2PSuggestionsStrip projectId={projectId} />

      {/* Top 5 At-Risk Activities */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-rose-500" />
            <h3 className="text-lg font-bold">أبرز الأنشطة المعرضة للخطر</h3>
            <span className="text-xs text-gray-500">(Top 5 — أسوأ أداء أولاً)</span>
          </div>
          {atRisk.length === 0 && (
            <span className="flex items-center text-xs text-emerald-700 gap-1 bg-emerald-50 px-3 py-1 rounded-full">
              <CheckCircle2 className="w-4 h-4" /> لا توجد أنشطة في الخطر حالياً
            </span>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="at-risk-grid">
          {atRisk.map((item) => (
            <AtRiskCard
              key={item.activity_code}
              item={item}
              onInspect={() => setDrawerActivity(item)}
            />
          ))}
        </div>
      </div>

      {/* All Activities Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b bg-gray-50">
          <h3 className="text-lg font-bold flex items-center gap-2">
            <ListChecks className="w-5 h-5 text-indigo-600" />
            كل الأنشطة <span className="text-sm font-normal text-gray-500">({activities.length})</span>
          </h3>
        </div>
        <ActivitiesTable activities={activities} />
      </div>

      {/* Inline Action Drawer */}
      {drawerActivity && (
        <ActionDrawer
          item={drawerActivity}
          onClose={() => setDrawerActivity(null)}
          onExecute={executeAction}
          executingAction={executingAction}
        />
      )}
    </div>
  );
};

// ─── S2P Suggestions Strip — Sprint A (live AI recommendations) ───

const S2PSuggestionsStrip = ({ projectId }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (alive) setLoading(true);
      try {
        const r = await axiosInstance.get(`/pece/s2p/suggestions?limit=5`);
        if (alive) {
          const items = (r.data || []).filter(
            (s) => !projectId || s.project_id === projectId,
          );
          setRows(items.slice(0, 3));
        }
      } catch (e) {
        // suggestions are optional — failing silently keeps PRS usable.
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [projectId]);

  const apply = async (decisionId) => {
    setActing(decisionId);
    try {
      await axiosInstance.post(`/pece/s2p/decisions/${decisionId}/approve`);
      toast.success('تم تطبيق التوصية');
      setRows((r) => r.filter((x) => x.id !== decisionId));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فشل تطبيق التوصية');
    } finally {
      setActing(null);
    }
  };

  if (loading || rows.length === 0) return null;
  return (
    <div data-testid="s2p-strip" className="rounded-xl border border-indigo-200 bg-gradient-to-r from-indigo-50 to-violet-50 p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-indigo-600" />
          <h3 className="text-sm font-bold text-indigo-900">S2P Smart Recommendations</h3>
          <span className="text-xs text-indigo-500">({rows.length} active)</span>
        </div>
      </div>
      <div className="space-y-2" data-testid="s2p-rows">
        {rows.map((s) => (
          <div key={s.id} data-testid={`s2p-row-${s.id}`}
                className="flex items-center justify-between rounded-lg bg-white border border-indigo-100 px-4 py-3">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-mono bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded">
                  {s.action_type || s.tier}
                </span>
                <span className="text-xs font-medium text-slate-600">
                  Confidence: {Math.round((s.confidence || 0) * 100)}%
                </span>
              </div>
              <div className="text-sm text-slate-900">
                {s.reasoning || s.title || s.description || `Decision ${s.id?.slice(0, 8)}`}
              </div>
              {s.activity_code && (
                <div className="text-xs text-slate-500 mt-0.5">Activity: {s.activity_code}</div>
              )}
            </div>
            <button
              data-testid={`s2p-apply-${s.id}`}
              disabled={acting === s.id}
              onClick={() => apply(s.id)}
              className="ml-4 inline-flex items-center gap-1 rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-50">
              <Wand2 className="w-3 h-3" />
              {acting === s.id ? '...' : 'Apply'}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};


// ─── Project PRS Big Card ─────────────────────────────────────────────────

const ProjectPRSCard = ({ project }) => {
  if (!project) return null;
  const cls = PROJECT_LABEL_COLORS[project.label] || PROJECT_LABEL_COLORS.critical;
  return (
    <div
      data-testid="project-prs-card"
      className={`rounded-xl border-2 p-6 ${cls}`}
    >
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider opacity-70 font-semibold">
            Project PRS · weighted by {project.weight_by}
          </div>
          <div className="flex items-baseline gap-3 mt-2">
            <div className="text-6xl font-bold" data-testid="project-prs-score">
              {project.prs?.toFixed(1)}
            </div>
            <div className="text-2xl font-semibold opacity-80">/ 100</div>
          </div>
          <div className="mt-1 text-sm font-semibold uppercase tracking-wider" data-testid="project-prs-label">
            {project.label}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <Pill k="Activities" v={project.activities_count} />
          <Pill k="Ready" v={project.by_label?.ready_to_execute || 0} good />
          <Pill k="Risk" v={project.by_label?.risk || 0} warn />
          <Pill k="Not Ready" v={project.by_label?.not_ready || 0} bad />
        </div>
      </div>
    </div>
  );
};

const Pill = ({ k, v, good, warn, bad }) => (
  <div className={`rounded-lg px-3 py-2 bg-white/70 ${
    good ? 'text-emerald-700' : warn ? 'text-orange-700' : bad ? 'text-rose-700' : 'text-gray-700'
  }`}>
    <div className="text-[10px] uppercase opacity-70">{k}</div>
    <div className="font-bold">{v}</div>
  </div>
);

// ─── At-Risk Activity Card ────────────────────────────────────────────────

const AtRiskCard = ({ item, onInspect }) => {
  const cls = TIER_COLORS[item.label] || TIER_COLORS.not_ready;
  return (
    <div
      data-testid={`at-risk-card-${item.activity_code}`}
      className={`rounded-lg border ${cls.border} ${cls.bg} p-4 hover:shadow-md transition cursor-pointer`}
      onClick={onInspect}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold truncate">{item.activity_code}</div>
          <div className="text-xs text-gray-600 truncate">{item.activity_name || '—'}</div>
        </div>
        <div className={`text-2xl font-bold ${cls.text}`}>
          {item.prs?.toFixed(0)}
        </div>
      </div>
      <div className="flex items-center gap-1.5 mb-3">
        <BreakdownBar label="M" value={item.breakdown?.material} />
        <BreakdownBar label="L" value={item.breakdown?.labour} />
        <BreakdownBar label="E" value={item.breakdown?.equipment} />
      </div>
      <div className="flex flex-wrap gap-1.5">
        {(item.suggested_actions || []).slice(0, 2).map((a) => (
          <span
            key={a.code}
            className={`text-[10px] px-2 py-0.5 rounded-full border ${URGENCY_PILLS[a.urgency] || URGENCY_PILLS.low}`}
          >
            {ACTION_ICONS[a.code]} {a.code.replace(/_/g, ' ')} · {fmtConfidence(a.confidence)}
          </span>
        ))}
      </div>
      <div className="mt-3 flex items-center justify-between text-[11px] text-gray-500">
        <span>{item.correlation_id ? `↳ ${item.event_chain_summary?.length || 0} events` : 'no events'}</span>
        <span className="flex items-center gap-1 text-indigo-600 font-semibold">
          View <ChevronRight className="w-3 h-3" />
        </span>
      </div>
    </div>
  );
};

const BreakdownBar = ({ label, value }) => {
  const v = Math.min(100, Math.max(0, value || 0));
  const tone = v >= 80 ? 'bg-emerald-500' : v >= 50 ? 'bg-amber-500' : 'bg-rose-500';
  return (
    <div className="flex-1 flex items-center gap-1.5 text-[10px]" title={`${label}: ${fmtPct(v)}`}>
      <span className="font-mono opacity-60 w-3">{label}</span>
      <div className="flex-1 h-1.5 bg-white rounded-full overflow-hidden">
        <div className={`h-full ${tone}`} style={{ width: `${v}%` }} />
      </div>
    </div>
  );
};

// ─── Activities Table ─────────────────────────────────────────────────────

const ActivitiesTable = ({ activities }) => {
  if (!activities.length) {
    return <div className="p-8 text-center text-gray-500 text-sm">لا توجد أنشطة بعد. ابدأ بإضافة Schedule + Resource Plan.</div>;
  }
  return (
    <table className="w-full text-sm" data-testid="activities-table">
      <thead className="bg-gray-50 border-b">
        <tr className="text-xs uppercase text-gray-600">
          <th className="px-4 py-3 text-right">Activity</th>
          <th className="px-4 py-3">PRS</th>
          <th className="px-4 py-3">Material</th>
          <th className="px-4 py-3">Labour</th>
          <th className="px-4 py-3">Equipment</th>
          <th className="px-4 py-3">Duration</th>
          <th className="px-4 py-3">Status</th>
        </tr>
      </thead>
      <tbody className="divide-y">
        {activities.map((a) => {
          const cls = TIER_COLORS[a.label] || TIER_COLORS.not_ready;
          return (
            <tr key={a.activity_code} className="hover:bg-gray-50" data-testid={`activity-row-${a.activity_code}`}>
              <td className="px-4 py-3">
                <div className="font-mono text-xs font-semibold">{a.activity_code}</div>
                <div className="text-xs text-gray-500">{a.activity_name || '—'}</div>
              </td>
              <td className="px-4 py-3 text-center">
                <div className={`inline-flex items-center justify-center w-12 h-12 rounded-full ${cls.bg} ${cls.text} font-bold border ${cls.border}`}>
                  {a.prs?.toFixed(0)}
                </div>
              </td>
              <td className="px-4 py-3 text-center text-xs">{fmtPct(a.mat_pct)}</td>
              <td className="px-4 py-3 text-center text-xs">{fmtPct(a.lab_pct)}</td>
              <td className="px-4 py-3 text-center text-xs">{fmtPct(a.eq_pct)}</td>
              <td className="px-4 py-3 text-center text-xs">{a.duration_days} يوم</td>
              <td className="px-4 py-3 text-center">
                <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] font-semibold ${cls.text}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${cls.dot}`} />
                  {a.label}
                </span>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

// ─── Inline Action Drawer ─────────────────────────────────────────────────

const ActionDrawer = ({ item, onClose, onExecute, executingAction }) => (
  <div
    data-testid="action-drawer"
    className="fixed inset-0 z-50 bg-black/40 flex items-end md:items-center justify-center p-4"
    onClick={onClose}
  >
    <div
      className="bg-white rounded-xl w-full max-w-2xl max-h-[85vh] overflow-y-auto"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="px-6 py-4 border-b flex items-center justify-between sticky top-0 bg-white">
        <div>
          <div className="text-xs text-indigo-600 font-bold uppercase tracking-wider">
            {item.activity_code}
          </div>
          <h3 className="text-lg font-bold">{item.activity_name || '—'}</h3>
        </div>
        <button
          data-testid="action-drawer-close"
          onClick={onClose}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="p-6 space-y-5">
        {/* PRS Score */}
        <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
          <div className="text-5xl font-bold">{item.prs?.toFixed(0)}</div>
          <div>
            <div className="font-semibold uppercase text-sm">{item.label}</div>
            <div className="text-xs text-gray-500">PRS Score</div>
          </div>
          <div className="ml-auto grid grid-cols-3 gap-2 text-xs">
            <Metric label="Material" v={item.breakdown?.material} />
            <Metric label="Labour" v={item.breakdown?.labour} />
            <Metric label="Equipment" v={item.breakdown?.equipment} />
          </div>
        </div>

        {/* Missing */}
        {item.missing && Object.keys(item.missing).length > 0 && (
          <div>
            <h4 className="text-sm font-bold mb-2 text-gray-700">النواقص</h4>
            <div className="grid grid-cols-3 gap-2">
              {item.missing.material_qty > 0 && (
                <MissingBox label="مواد" value={`${item.missing.material_qty} قطعة`} />
              )}
              {item.missing.labour_qty > 0 && (
                <MissingBox label="عمالة" value={`${item.missing.labour_qty} عامل`} />
              )}
              {item.missing.equipment_qty > 0 && (
                <MissingBox label="معدات" value={`${item.missing.equipment_qty} وحدة`} />
              )}
            </div>
          </div>
        )}

        {/* Suggested Actions */}
        <div>
          <h4 className="text-sm font-bold mb-2 text-gray-700 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-amber-500" /> الإجراءات المقترحة
          </h4>
          <div className="space-y-2">
            {(item.suggested_actions || []).map((a) => {
              const isExecuting = executingAction === `${item.activity_code}:${a.code}`;
              const canExecute = a.code === 'CREATE_PPR_MATERIAL';
              return (
                <div
                  key={a.code}
                  className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
                >
                  <span className="text-2xl">{ACTION_ICONS[a.code]}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm">{a.text}</div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full border ${URGENCY_PILLS[a.urgency]}`}>
                        {a.urgency}
                      </span>
                      <span className="text-[10px] text-gray-500">
                        Confidence: <span className="font-bold">{fmtConfidence(a.confidence)}</span>
                      </span>
                    </div>
                  </div>
                  <button
                    data-testid={`action-execute-${a.code}`}
                    disabled={!canExecute || isExecuting}
                    onClick={() => onExecute(item.activity_code, a.code)}
                    className={`px-3 py-2 rounded-lg text-sm font-semibold flex items-center gap-1.5 ${
                      canExecute
                        ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                        : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    {isExecuting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
                    {canExecute ? 'تنفيذ' : 'قريباً'}
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Event Chain */}
        {item.event_chain_summary && item.event_chain_summary.length > 0 && (
          <div>
            <h4 className="text-sm font-bold mb-2 text-gray-700 flex items-center gap-2">
              <Activity className="w-4 h-4 text-indigo-600" /> سلسلة الأحداث
              {item.correlation_id && (
                <a
                  href={`/ecp?correlation_id=${item.correlation_id}`}
                  className="text-[10px] text-indigo-600 hover:underline flex items-center gap-1"
                >
                  ECP <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </h4>
            <div className="flex flex-wrap items-center gap-1">
              {item.event_chain_summary.map((ev, i) => (
                <React.Fragment key={ev}>
                  <span className="text-[10px] font-mono bg-indigo-50 text-indigo-700 px-2 py-1 rounded">
                    {ev}
                  </span>
                  {i < item.event_chain_summary.length - 1 && (
                    <ChevronRight className="w-3 h-3 text-gray-400" />
                  )}
                </React.Fragment>
              ))}
            </div>
            {item.latest_ppr && (
              <div className="mt-2 text-[11px] text-gray-500">
                Latest PPR: <span className="font-bold">{item.latest_ppr.ppr_no}</span> ({item.latest_ppr.status})
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  </div>
);

const Metric = ({ label, v }) => (
  <div className="text-center">
    <div className="text-[10px] uppercase text-gray-500">{label}</div>
    <div className="font-bold">{fmtPct(v)}</div>
  </div>
);

const MissingBox = ({ label, value }) => (
  <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-center">
    <div className="text-[10px] uppercase text-rose-600">{label}</div>
    <div className="font-bold text-rose-700 text-sm">{value}</div>
  </div>
);

export default PECEPRSTab;
